import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Vista Fusion Studio";
const SITE_URL = "https://www.vistafusionstudio.com";
const DEFAULT_DESCRIPTION =
  "Vista Fusion Studio is your go-to destination for all your interior needs. From complete home and office design, to renovations and modular solutions — we bring your vision to life.";
const DEFAULT_IMAGE =
  "https://www.vistafusionstudio.com/public-images/vista-images/Images/vistalogo.png";

export type SeoProps = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
};

const toCanonical = (path: string) => {
  if (!path.startsWith("/")) return `${SITE_URL}/${path}`;
  return `${SITE_URL}${path}`;
};

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path,
  image,
  type = "website",
  noIndex = false,
  jsonLd,
}) => {
  const location = useLocation();
  const canonicalPath = path ?? location.pathname;
  const canonicalUrl = toCanonical(canonicalPath);
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const metaDescription = description ?? DEFAULT_DESCRIPTION;
  const metaImage = image ?? DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={metaImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {jsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default Seo;
